// How much of this order slot is already spoken for.
//
// The allocation modal listed what an order still has ROOM for and nothing
// else, so "30 × 100 m coil" read as a fresh order on a slot that might be
// three-quarters built. Open Lines has a Plan column that answers exactly this
// — planned against ordered, with a bar — and the modal is where the decision
// is actually made.
//
// Every figure is already on the item the allocation endpoint returns. The
// only one that has to be worked out is how much OTHER products have promised,
// and it falls out of the open count:
//
//   unitsOpen = planned − produced − heldByEverybody
//   heldByOthers = planned − produced − unitsOpen − heldByThisJob

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * The state of one delivery-plan slot, in units.
 *
 * @param it   an item from the product-allocation candidates
 * @returns {{ planned, produced, heldHere, heldElsewhere, open, done, pct, settled }}
 *          `settled` is everything no longer up for grabs — made, or promised
 *          by anybody. `done` is only what physically exists.
 */
export function slotProgress(it) {
  const planned = Math.max(0, num(it?.qtyPlanned));
  const produced = Math.max(0, num(it?.qtyProduced));
  const open = Math.max(0, num(it?.unitsOpen));
  const heldHere = Math.max(0, num(it?.unitsHeldByThisJob));

  // Never negative: a plan edited down after a promise was made can leave the
  // arithmetic upside down, and a "-2 promised elsewhere" on screen is worse
  // than showing none.
  const heldElsewhere = Math.max(0, planned - produced - open - heldHere);

  const settled = Math.min(planned, produced + heldHere + heldElsewhere);
  const pct = planned > 0 ? Math.min(100, Math.round((settled / planned) * 100)) : 0;

  return { planned, produced, heldHere, heldElsewhere, open, done: produced, settled, pct };
}

/**
 * The same thing as a sentence, or null when there is nothing worth saying.
 *
 * A slot where nothing has happened yet gets no line at all — the point is to
 * flag the ones that are part-built, not to add a "0 made" to every row.
 */
export function slotProgressNote(it) {
  const p = slotProgress(it);
  if (!p.planned) return null;
  if (!p.produced && !p.heldHere && !p.heldElsewhere) return null;

  const parts = [];
  if (p.produced) parts.push(`${p.produced} made`);
  if (p.heldHere) parts.push(`${p.heldHere} promised by this product`);
  if (p.heldElsewhere) parts.push(`${p.heldElsewhere} promised elsewhere`);

  return `of ${p.planned} planned · ${parts.join(' · ')}`;
}

/** Colour for the bar: green once the slot is covered, amber while it is not. */
export const slotProgressVariant = it => (slotProgress(it).pct >= 100 ? 'success' : 'warning');
